import './bootstrap';

import { createInertiaApp } from '@inertiajs/react';
import { createRoot } from 'react-dom/client';
import { HeroUIProvider } from '@heroui/system';
import DropdownActionProvider from '@module:shared/_providers/DropdownActionProvider';
import { registerSW } from 'virtual:pwa-register';
import * as Sentry from '@sentry/react';
import ChatDrawer from '@module:shared/_components/ChatDrawer';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const appEnv = import.meta.env.VITE_APP_ENV || 'local'; // Default to 'local' if not set

if (sentryDsn && (appEnv === 'production' || appEnv === 'staging')) {
    Sentry.init({
        dsn: sentryDsn,
        environment: appEnv,
        // Setting this option to true will send default PII data to Sentry.
        // For example, automatic IP address collection on events
        sendDefaultPii: true,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],
        // Tracing
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
            /^https:\/\/test\.rekyndness\.id/,
            /^https:\/\/donation-test\.rekyndness\.id/,
            /^https:\/\/rekyndness\.id/,
        ],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}

createInertiaApp({
    resolve: async name => {
        // Use root-relative paths for clarity and consistency.
        const pages = import.meta.glob([
            '/resources/js/Pages/**/*.jsx',
            '/modules/*/Resources/Views/Js/Pages/**/*.jsx',
        ]);

        let path;

        if (name.includes('::')) {
            const [module, pageName] = name.split('::');
            // Find the full path for the namespaced component.
            // Accommodates both '/PageName.jsx' and '/PageName/page.jsx' conventions.
            path = Object.keys(pages).find(
                p =>
                    p ===
                        `/modules/${module}/Resources/Views/Js/Pages/${pageName}.jsx` ||
                    p ===
                        `/modules/${module}/Resources/Views/Js/Pages/${pageName}/page.jsx`
            );
        } else {
            // For non-namespaced names, first check the standard /resources/js/Pages directory.
            path = Object.keys(pages).find(
                p => p === `/resources/js/Pages/${name}.jsx`
            );

            // Fallback for router.reload(): If not in standard pages, search in all modules.
            if (!path) {
                // This finds a page in ANY module that matches the name.
                path = Object.keys(pages).find(
                    p =>
                        p.endsWith(`/${name}.jsx`) ||
                        p.endsWith(`/${name}/page.jsx`)
                );
            }
        }

        const resolver = pages[path];

        if (!resolver) {
            throw new Error(`Page component '${name}' could not be resolved.`);
        }

        const pageModule = await resolver();

        // A module can have a 'default' export, or it can be the export itself.
        // This handles both cases gracefully.
        return pageModule.default || pageModule;
    },
    onError: error => {
        console.error('error:', error);

        // Misal: tangani status 500
        if (error?.response?.status === 500) {
            window.location.href = '/error';
        }

        // Misal: tampilkan notifikasi custom
        if (error?.response?.status === 404) {
            alert('Halaman tidak ditemukan!');
        }
    },
    setup({ el, App, props }) {
        registerSW({
            immediate: true,
            onNeedRefresh() {},
            onOfflineReady() {},
        });
        createRoot(el).render(
            <HeroUIProvider>
                <DropdownActionProvider>
                    <App {...props} />
                    <ChatDrawer />
                </DropdownActionProvider>
            </HeroUIProvider>
        );
    },
    title: title => `${title} - Rekyndness`,
    progress: {
        delay: 250,
        color: '#0a945f',
        includeCSS: true,
        showSpinner: true,
    },
});
